import { Truck, MapPin, Clock, IndianRupee, Package, BadgeCheck, Globe, MapPinned } from "lucide-react";

const deliveryOptions = [
  {
    icon: Truck,
    title: "Bangalore Local",
    subtitle: "Within 30 km of our store",
    description: "Quick and reliable same-day delivery across Bangalore through our tie-up with leading local delivery apps and logistics partners.",
    partners: ["Porter", "Delhivery", "Uncle Delivery", "Uber", "Online Apps"],
    pastel: "pastel-green",
    accent: "var(--mp-text)",
  },
  {
    icon: BadgeCheck,
    title: "Free Delivery",
    subtitle: "Billing above Rs. 50,000",
    description: "All orders with billing above Rs. 50,000 within 2 km of our Chickpet store qualify for complimentary doorstep delivery. No additional charges.",
    highlight: "Within 2 km radius of our Chickpet store",
    pastel: "pastel-yellow",
    accent: "var(--mp-copper-deep)",
  },
  {
    icon: Package,
    title: "South India",
    subtitle: "4 Working Days from Dispatch",
    description: "We dispatch goods across all of South India through trusted local transport services and bus services. Delivery within 4 working days from the date of dispatch. Transport is charged per parcel — Rs. 100 by local lorry, Rs. 150 by bus or travels.",
    pastel: "pastel-peach",
    accent: "var(--mp-copper)",
  },
  {
    icon: MapPinned,
    title: "Across India",
    subtitle: "Pan-India Delivery",
    description: "We deliver across the length and breadth of India through trusted logistics partners ensuring safe and timely delivery of your fabric orders.",
    partners: ["VRL Logistics", "Trusted Transport Partners"],
    pastel: "pastel-lavender",
    accent: "var(--mp-copper-deep)",
  },
  {
    icon: Globe,
    title: "International Shipping",
    subtitle: "Worldwide Delivery",
    description: "We ship globally through premium international carriers ensuring your fabrics reach any corner of the world safely. Custom documentation and packaging for international orders.",
    partners: ["FedEx", "DHL", "International Cargo Carriers"],
    pastel: "pastel-blue",
    accent: "var(--mp-accent-warm)",
  },
];

export default function DeliverySection() {
  return (
    <section
      id="delivery"
      className="py-20 lg:py-32"
      style={{ backgroundColor: "var(--mp-surface)" }}
      data-testid="delivery-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14">
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mb-4"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Fast & Reliable
          </p>
          <h1
            className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="delivery-heading"
          >
            Delivery & Shipping
          </h1>
          <div className="section-divider" />
          <p
            className="mt-4 text-base leading-relaxed max-w-2xl"
            style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
          >
            From local doorstep delivery in Bangalore to outstation dispatch across South India
            and international shipping worldwide, we ensure your fabrics reach you safely and on time.
          </p>
        </div>

        {/* Delivery options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {deliveryOptions.map((option, idx) => {
            const Icon = option.icon;
            return (
              <div
                key={option.title}
                className={`${option.pastel} product-card border border-[var(--mp-line)] rounded-sm p-7 animate-fade-in-up`}
                style={{ animationDelay: `${idx * 120}ms` }}
                data-testid={`delivery-card-${idx}`}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: `color-mix(in srgb, ${option.accent} 12%, transparent)` }}
                >
                  <Icon className="w-6 h-6" style={{ color: option.accent }} />
                </div>

                <h3
                  className="text-xl md:text-2xl tracking-tight font-medium mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                >
                  {option.title}
                </h3>
                <p
                  className="text-xs uppercase tracking-[0.15em] font-semibold mb-4"
                  style={{ color: option.accent, fontFamily: "'Manrope', sans-serif" }}
                >
                  {option.subtitle}
                </p>

                <p
                  className="text-sm leading-relaxed mb-4"
                  style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                >
                  {option.description}
                </p>

                {/* Partners list */}
                {option.partners && (
                  <div className="flex flex-wrap gap-2">
                    {option.partners.map((partner) => (
                      <span
                        key={partner}
                        className="text-[11px] font-semibold px-3 py-1.5 rounded-sm bg-white/70 border border-[var(--mp-line)]"
                        style={{ color: option.accent, fontFamily: "'Manrope', sans-serif" }}
                      >
                        {partner}
                      </span>
                    ))}
                  </div>
                )}

                {/* Highlight */}
                {option.highlight && (
                  <div className="flex items-center gap-2 mt-1 border border-[var(--mp-line)] bg-[var(--mp-surface)] rounded-sm px-4 py-3">
                    <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: option.accent }} />
                    <span
                      className="text-xs font-medium"
                      style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                    >
                      {option.highlight}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Delivery timeline */}
        <div className="bg-white border border-[var(--mp-line)] rounded-sm p-6 sm:p-8" data-testid="delivery-timeline">
          <h3
            className="text-xl md:text-2xl tracking-tight font-medium mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
          >
            Estimated Delivery Timeline
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Bangalore Local */}
            <div className="flex items-start gap-3 pastel-green rounded-sm p-4 border border-[var(--mp-line)]">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#10101015" }}>
                <Clock className="w-4 h-4" style={{ color: "var(--mp-text-strong)" }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                  Same Day
                </p>
                <p className="text-xs mt-0.5" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                  Bangalore local within 30 km via delivery apps
                </p>
              </div>
            </div>

            {/* Free delivery */}
            <div className="flex items-start gap-3 pastel-yellow rounded-sm p-4 border border-[var(--mp-line)]">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#8A5A2415" }}>
                <IndianRupee className="w-4 h-4" style={{ color: "var(--mp-copper-deep)" }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                  Free Delivery
                </p>
                <p className="text-xs mt-0.5" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                  Billing above Rs. 50,000 within 2 km
                </p>
              </div>
            </div>

            {/* South India */}
            <div className="flex items-start gap-3 pastel-peach rounded-sm p-4 border border-[var(--mp-line)]">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#B8733315" }}>
                <Clock className="w-4 h-4" style={{ color: "var(--mp-copper)" }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                  4 Working Days
                </p>
                <p className="text-xs mt-0.5" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                  South India via local transports & bus services
                </p>
              </div>
            </div>

            {/* Across India */}
            <div className="flex items-start gap-3 pastel-lavender rounded-sm p-4 border border-[var(--mp-line)]">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#8A5A2415" }}>
                <MapPinned className="w-4 h-4" style={{ color: "var(--mp-copper-deep)" }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                  Pan-India
                </p>
                <p className="text-xs mt-0.5" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                  Across India via VRL Logistics & partners
                </p>
              </div>
            </div>

            {/* International */}
            <div className="flex items-start gap-3 pastel-blue rounded-sm p-4 border border-[var(--mp-line)]">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#5C534715" }}>
                <Globe className="w-4 h-4" style={{ color: "#5C5347" }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                  International
                </p>
                <p className="text-xs mt-0.5" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                  Worldwide via FedEx, DHL & cargo carriers
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
